import { useState, useEffect } from 'react'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  XMarkIcon,
  PlusIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'

export default function Calendar() {
  const { user } = useAuth()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)
  const [view, setView] = useState('month') // 'month', 'week', 'day'
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({ total: 0, confirmed: 0, pending: 0, cancelled: 0, upcoming: 0 })

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  // Fetch appointments from API
  useEffect(() => {
    fetchAppointments()
    fetchStats()
  }, [currentDate, user])

  const fetchAppointments = async () => {
    try {
      setLoading(true)
      setError(null)

      // Calculate start and end dates for the current month view
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

      const startStr = startDate.toISOString().split('T')[0]
      const endStr = endDate.toISOString().split('T')[0]

      const token = localStorage.getItem('auth_token')
      const response = await fetch(
        `/api/customer/appointments?start_date=${startStr}&end_date=${endStr}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch appointments')
      }

      const data = await response.json()

      // Transform appointments to match calendar format
      const transformedAppointments = data.map(apt => ({
        id: apt.id,
        date: new Date(apt.appointment_datetime).toISOString().split('T')[0],
        time: new Date(apt.appointment_datetime).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }),
        title: `Appointment with ${apt.doctor?.name || 'Doctor'}`,
        client: apt.patient_name,
        description: `${apt.service?.name || 'Consultation'} - ${apt.duration_minutes} minutes`,
        status: apt.status,
        rawData: apt
      }))

      setAppointments(transformedAppointments)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching appointments:', err)
      setError(err.message)
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch('/api/customer/appointments/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setStats(data)
      }
    } catch (err) {
      console.error('Error fetching stats:', err)
    }
  }

  const handleDeleteAppointment = async (id) => {
    if (!confirm('Are you sure you want to cancel this appointment?')) {
      return
    }

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`/api/customer/appointments/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to cancel appointment')
      }

      // Refresh appointments
      await fetchAppointments()
      await fetchStats()
    } catch (err) {
      console.error('Error cancelling appointment:', err)
      alert('Failed to cancel appointment: ' + err.message)
    }
  }

  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    return new Date(year, month + 1, 0).getDate()
  }

  // Get first day of month (0 = Sunday, 6 = Saturday)
  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    return new Date(year, month, 1).getDay()
  }

  // Navigate to previous month
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
  }

  // Navigate to next month
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
  }

  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date())
    setSelectedDate(new Date())
  }

  // Check if date is today
  const isToday = (day) => {
    const today = new Date()
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    )
  }

  // Check if date is selected
  const isSelected = (day) => {
    if (!selectedDate) return false
    return (
      day === selectedDate.getDate() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getFullYear() === selectedDate.getFullYear()
    )
  }

  // Get appointments for a specific day
  const getAppointmentsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return appointments.filter(apt => apt.date === dateStr)
  }

  // Handle date selection
  const handleDateClick = (day) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
    setSelectedDate(newDate)
  }

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate)
    const firstDay = getFirstDayOfMonth(currentDate)
    const days = []

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(null)
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }

    return days
  }

  const calendarDays = generateCalendarDays()

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Calendar Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <p className="text-sm text-blue-100">Appointment Calendar</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-all text-sm font-semibold"
            >
              Today
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={previousMonth}
            className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-all"
            aria-label="Previous month"
          >
            <ChevronLeftIcon className="h-5 w-5 text-white" />
          </button>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setView('day')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                view === 'day'
                  ? 'bg-white text-blue-600 shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              Day
            </button>
            <button
              onClick={() => setView('week')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                view === 'week'
                  ? 'bg-white text-blue-600 shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setView('month')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                view === 'month'
                  ? 'bg-white text-blue-600 shadow-lg'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              Month
            </button>
          </div>

          <button
            onClick={nextMonth}
            className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-all"
            aria-label="Next month"
          >
            <ChevronRightIcon className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">Error loading appointments: {error}</p>
        </div>
      )}

      {/* Calendar Grid */}
      <div className="p-6">
        {/* Day Names */}
        <div className="grid grid-cols-7 gap-2 mb-3">
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="text-center text-sm font-semibold text-gray-600 py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="aspect-square" />
            }

            const dayAppointments = getAppointmentsForDay(day)
            const isCurrentDay = isToday(day)
            const isSelectedDay = isSelected(day)

            return (
              <button
                key={day}
                onClick={() => handleDateClick(day)}
                className={`aspect-square p-2 rounded-xl border-2 transition-all transform hover:scale-105 relative group ${
                  isCurrentDay
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-blue-600 shadow-lg'
                    : isSelectedDay
                    ? 'bg-gradient-to-br from-blue-100 to-indigo-100 border-blue-400 text-blue-900'
                    : dayAppointments.length > 0
                    ? 'bg-purple-50 border-purple-200 text-purple-900 hover:bg-purple-100'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col h-full">
                  <span className={`text-sm font-semibold mb-1 ${
                    isCurrentDay ? 'text-white' : ''
                  }`}>
                    {day}
                  </span>
                  {dayAppointments.length > 0 && (
                    <div className="flex-1 flex flex-col items-center justify-center">
                      <div className={`w-2 h-2 rounded-full ${
                        isCurrentDay ? 'bg-white' : 'bg-purple-500'
                      } mb-1`} />
                      <span className={`text-xs font-medium ${
                        isCurrentDay ? 'text-white' : 'text-purple-600'
                      }`}>
                        {dayAppointments.length}
                      </span>
                    </div>
                  )}
                </div>

                {/* Hover tooltip */}
                {dayAppointments.length > 0 && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-gray-900 text-white text-xs rounded-lg py-1 px-2 whitespace-nowrap shadow-xl">
                      {dayAppointments.length} appointment{dayAppointments.length > 1 ? 's' : ''}
                    </div>
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Appointment Details Section */}
      {selectedDate && (
        <div className="border-t border-gray-200 bg-gray-50 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              {selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
          </div>

          {/* Appointments List */}
          <div className="space-y-3">
            {getAppointmentsForDay(selectedDate.getDate()).length > 0 ? (
              getAppointmentsForDay(selectedDate.getDate()).map((appointment, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-1.5 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full" />
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-gray-900">
                            {appointment.title || 'Appointment'}
                          </h4>
                          <div className="flex items-center space-x-4 mt-1">
                            <div className="flex items-center space-x-1 text-xs text-gray-600">
                              <ClockIcon className="h-3.5 w-3.5" />
                              <span>{appointment.time || 'No time set'}</span>
                            </div>
                            {appointment.client && (
                              <div className="flex items-center space-x-1 text-xs text-gray-600">
                                <UserIcon className="h-3.5 w-3.5" />
                                <span>{appointment.client}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {appointment.description && (
                        <p className="text-xs text-gray-600 ml-3.5 mt-1">
                          {appointment.description}
                        </p>
                      )}
                      {appointment.status && (
                        <div className="ml-3.5 mt-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            appointment.status === 'confirmed'
                              ? 'bg-green-100 text-green-700'
                              : appointment.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {appointment.status === 'confirmed' && (
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                            )}
                            {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                          </span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteAppointment(appointment.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Cancel appointment"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CalendarDaysIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No appointments scheduled for this day</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Appointment Statistics */}
      <div className="border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white p-6">
        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Loading statistics...</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-gray-200">
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
              <p className="text-xs text-gray-600 mt-1">Total Appointments</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-gray-200">
              <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
              <p className="text-xs text-gray-600 mt-1">Confirmed</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-gray-200">
              <p className="text-2xl font-bold text-yellow-600">{stats.upcoming}</p>
              <p className="text-xs text-gray-600 mt-1">Upcoming</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
